import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  Alert, BaseBarChart, BaseDonutChart, Button, Card, KpiCard, Spinner,
} from '@kss/ui';
import { apiClient } from '@kss/api';
import {
  AlertTriangle, Boxes, ClipboardCheck, IndianRupee, PackageOpen, RefreshCw, ShoppingCart,
} from 'lucide-react';

/**
 * Inventory health dashboard.
 *
 * Joins three live endpoints:
 *   /master-data/Product         (catalogue, sellingPrice, costPrice, reorderLevel)
 *   /purchase/StockBalance       (per-warehouse on-hand qty)
 *   /master-data/ProductCategory (label lookup)
 *
 * Surfaces what an ops manager actually checks every morning: how many
 * SKUs are below reorder, what the stock value is, the top items by
 * value, and the distribution across categories.
 */

interface Product extends Record<string, unknown> {
  productId?: string;
  id?: number | string;
  code?: string;
  name?: string;
  sellingPrice?: number;
  costPrice?: number;
  reorderLevel?: number;
  productCategoryId?: string;
}

interface StockBalance extends Record<string, unknown> {
  productId?: string;
  warehouseId?: string;
  qty?: number;
  quantity?: number;
}

interface Category extends Record<string, unknown> {
  productCategoryId?: string;
  id?: number | string;
  name?: string;
}

interface ProductWithStock {
  product: Product;
  qty: number;
  reorderLevel: number;
  costPrice: number;
  sellingPrice: number;
  stockValue: number;
  category: string;
  status: 'OK' | 'Low' | 'Out';
}

export default function InventoryDashboard() {
  const navigate = useNavigate();
  const [products, setProducts] = useState<Product[]>([]);
  const [stock, setStock] = useState<StockBalance[]>([]);
  const [categories, setCategories] = useState<Category[]>([]);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  async function load() {
    setLoading(true);
    setErr(null);
    try {
      const [p, s, c] = await Promise.all([
        apiClient.get('/master-data/Product').then(pickList<Product>).catch(() => []),
        apiClient.get('/purchase/StockBalance').then(pickList<StockBalance>).catch(() => []),
        apiClient.get('/master-data/ProductCategory').then(pickList<Category>).catch(() => []),
      ]);
      setProducts(p);
      setStock(s);
      setCategories(c);
    } catch (e) {
      setErr(e instanceof Error ? e.message : 'Failed to load inventory');
    } finally {
      setLoading(false);
    }
  }
  useEffect(() => { void load(); }, []);

  // Aggregate stock per product across warehouses.
  const rows = useMemo<ProductWithStock[]>(() => {
    const stockByProduct = new Map<string, number>();
    for (const s of stock) {
      const id = String(s.productId ?? '');
      if (!id) continue;
      const q = Number(s.qty ?? s.quantity ?? 0);
      stockByProduct.set(id, (stockByProduct.get(id) ?? 0) + q);
    }
    const catName = new Map<string, string>();
    for (const c of categories) {
      catName.set(String(c.productCategoryId ?? c.id ?? ''), String(c.name ?? ''));
    }
    return products.map((p): ProductWithStock => {
      const id = String(p.productId ?? p.id ?? '');
      const qty = stockByProduct.get(id) ?? 0;
      const reorderLevel = Number(p.reorderLevel ?? 0);
      const costPrice = Number(p.costPrice ?? 0);
      const sellingPrice = Number(p.sellingPrice ?? 0);
      const stockValue = qty * costPrice;
      const status: ProductWithStock['status'] =
        qty <= 0 ? 'Out' : reorderLevel > 0 && qty <= reorderLevel ? 'Low' : 'OK';
      return {
        product: p,
        qty,
        reorderLevel,
        costPrice,
        sellingPrice,
        stockValue,
        category: catName.get(String(p.productCategoryId ?? '')) ?? '—',
        status,
      };
    });
  }, [products, stock, categories]);

  const kpis = useMemo(() => {
    const total = rows.length;
    const inStock = rows.filter((r) => r.status === 'OK').length;
    const low = rows.filter((r) => r.status === 'Low').length;
    const out = rows.filter((r) => r.status === 'Out').length;
    const stockValue = rows.reduce((s, r) => s + r.stockValue, 0);
    return { total, inStock, low, out, stockValue };
  }, [rows]);

  const topByValue = useMemo(
    () =>
      [...rows]
        .filter((r) => r.stockValue > 0)
        .sort((a, b) => b.stockValue - a.stockValue)
        .slice(0, 8)
        .map((r) => ({
          name: (r.product.name ?? r.product.code ?? '—').toString().slice(0, 18),
          value: Math.round(r.stockValue),
        })),
    [rows],
  );

  const valueByCategory = useMemo(() => {
    const m = new Map<string, number>();
    for (const r of rows) {
      m.set(r.category, (m.get(r.category) ?? 0) + r.stockValue);
    }
    return [...m.entries()]
      .map(([name, value]) => ({ name, value: Math.round(value) }))
      .sort((a, b) => b.value - a.value)
      .slice(0, 6);
  }, [rows]);

  const lowStock = useMemo(
    () =>
      rows
        .filter((r) => r.status === 'Low' || r.status === 'Out')
        .sort((a, b) => {
          if (a.status === 'Out' && b.status !== 'Out') return -1;
          if (b.status === 'Out' && a.status !== 'Out') return 1;
          return (a.qty - a.reorderLevel) - (b.qty - b.reorderLevel);
        })
        .slice(0, 25),
    [rows],
  );

  return (
    <div className="-m-6 space-y-3 p-[3px]">
      <div className="flex flex-wrap items-center gap-3 rounded-xl border border-border bg-surface-card px-4 py-3">
        <div className="min-w-0 flex-1">
          <h1 className="text-base font-semibold text-content-primary">Inventory health</h1>
          <p className="text-xs text-content-muted">
            Live snapshot · {products.length} products · {stock.length} stock rows
          </p>
        </div>
        <Button variant="outline" size="sm" onClick={load} disabled={loading}>
          <RefreshCw className={`h-3.5 w-3.5 ${loading ? 'animate-spin' : ''}`} /> Reload
        </Button>
        <Button size="sm" onClick={() => navigate('/panel/inventory/stock-take')}>
          <ClipboardCheck className="h-3.5 w-3.5" /> Stock take
        </Button>
      </div>

      {err && <Alert variant="danger">{err}</Alert>}

      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-5">
        <KpiCard
          title="Total SKUs"
          value={kpis.total.toLocaleString()}
          subtitle="Active products"
          icon={<Boxes className="h-5 w-5 text-info" />}
        />
        <KpiCard
          title="In stock"
          value={kpis.inStock.toLocaleString()}
          subtitle={`${kpis.total ? Math.round((kpis.inStock / kpis.total) * 100) : 0}% of catalogue`}
          icon={<PackageOpen className="h-5 w-5 text-success" />}
        />
        <KpiCard
          title="Low stock"
          value={kpis.low.toLocaleString()}
          subtitle="At or below reorder"
          icon={<AlertTriangle className={`h-5 w-5 ${kpis.low ? 'text-warning' : 'text-success'}`} />}
        />
        <KpiCard
          title="Out of stock"
          value={kpis.out.toLocaleString()}
          subtitle={kpis.out ? 'Action needed' : 'All clear'}
          icon={<AlertTriangle className={`h-5 w-5 ${kpis.out ? 'text-danger' : 'text-success'}`} />}
        />
        <KpiCard
          title="Stock value"
          value={`₹ ${inr(kpis.stockValue)}`}
          subtitle="At cost price"
          icon={<IndianRupee className="h-5 w-5 text-primary" />}
        />
      </div>

      <div className="grid gap-3 lg:grid-cols-2">
        <Card>
          <h3 className="mb-2 text-sm font-semibold text-content-primary">Top items by stock value</h3>
          {topByValue.length === 0 ? (
            <div className="flex h-[260px] items-center justify-center text-xs text-content-muted">
              No stock yet — run a stock take or seed sample data.
            </div>
          ) : (
            <BaseBarChart
              data={topByValue}
              bars={[{ dataKey: 'value', name: 'Value (₹)' }]}
              xAxisKey="name"
              height={260}
            />
          )}
        </Card>
        <Card>
          <h3 className="mb-2 text-sm font-semibold text-content-primary">Stock value by category</h3>
          {valueByCategory.length === 0 ? (
            <div className="flex h-[260px] items-center justify-center text-xs text-content-muted">
              No data yet
            </div>
          ) : (
            <BaseDonutChart data={valueByCategory} height={260} />
          )}
        </Card>
      </div>

      <Card>
        <div className="mb-2 flex items-center justify-between">
          <h3 className="text-sm font-semibold text-content-primary">
            Low stock &amp; out-of-stock
          </h3>
          <span className="text-xs text-content-muted">
            {lowStock.length} of {kpis.low + kpis.out} item(s)
          </span>
        </div>
        {loading && rows.length === 0 ? (
          <div className="flex items-center gap-2 p-4 text-sm text-content-muted">
            <Spinner /> Loading…
          </div>
        ) : lowStock.length === 0 ? (
          <div className="p-4 text-sm text-content-muted">
            Nothing to reorder right now. Set <em>reorderLevel</em> on a product to start tracking.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="text-xs text-content-muted">
                <tr className="border-b border-border">
                  <th className="px-2 py-2 text-left">Product</th>
                  <th className="px-2 py-2 text-left">Category</th>
                  <th className="px-2 py-2 text-right">On hand</th>
                  <th className="px-2 py-2 text-right">Reorder at</th>
                  <th className="px-2 py-2 text-right">Shortfall</th>
                  <th className="px-2 py-2 text-left">Status</th>
                  <th className="w-32 px-2 py-2" />
                </tr>
              </thead>
              <tbody>
                {lowStock.map((r) => {
                  const id = String(r.product.productId ?? r.product.id ?? '');
                  return (
                    <tr key={id} className="border-b border-border/40">
                      <td className="px-2 py-2">
                        <div className="font-medium">{r.product.name ?? r.product.code ?? id.slice(0, 8)}</div>
                        <div className="text-[11px] text-content-muted">{r.product.code ?? ''}</div>
                      </td>
                      <td className="px-2 py-2 text-content-secondary">{r.category}</td>
                      <td className="px-2 py-2 text-right tabular-nums">{r.qty}</td>
                      <td className="px-2 py-2 text-right tabular-nums">{r.reorderLevel || '—'}</td>
                      <td className="px-2 py-2 text-right tabular-nums">
                        {Math.max(0, r.reorderLevel - r.qty) || '—'}
                      </td>
                      <td className="px-2 py-2">
                        <StatusPill v={r.status} />
                      </td>
                      <td className="px-2 py-2 text-right">
                        <Link
                          to="/panel/purchase/orders/new"
                          className="inline-flex items-center gap-1 text-xs text-primary hover:underline"
                        >
                          <ShoppingCart size={12} /> Reorder
                        </Link>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </div>
  );
}

function StatusPill({ v }: { v: ProductWithStock['status'] }) {
  const cls =
    v === 'Out'
      ? 'border-danger/40 bg-danger/10 text-danger'
      : v === 'Low'
        ? 'border-warning/40 bg-warning/10 text-warning'
        : 'border-success/40 bg-success/10 text-success';
  return (
    <span className={`inline-flex rounded-full border px-2 py-0.5 text-[11px] ${cls}`}>{v}</span>
  );
}

function pickList<T>(r: { data: unknown }): T[] {
  const d = r.data;
  if (Array.isArray(d)) return d as T[];
  if (d && typeof d === 'object') {
    const obj = d as { items?: unknown[]; data?: unknown[] | { items?: unknown[] } };
    if (Array.isArray(obj.items)) return obj.items as T[];
    if (Array.isArray(obj.data)) return obj.data as T[];
    if (obj.data && Array.isArray((obj.data as { items?: unknown[] }).items)) {
      return (obj.data as { items: unknown[] }).items as T[];
    }
  }
  return [];
}

function inr(n: number): string {
  if (!Number.isFinite(n)) return '0';
  return n.toLocaleString('en-IN', { minimumFractionDigits: 0, maximumFractionDigits: 0 });
}
